import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { DestroyRef, Injectable, Injector, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { DEFAULT_AUTH_TOKEN_HEADER_KEY } from '@phoenix/common';
import { AdminUiConfig, ErrorCode } from '@phx-erp/shared';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { getAppConfig } from '../../app.config';
import { DataService } from '../../data/services/data.service';
import { AuthService } from '../../shared/services/auth.service';
import { AUTH_TOKEN_KEY } from '../constants/settings-keys';
import { NotificationService } from '../services/notification/notification.service';
import { AUTH_REDIRECT_PARAM } from './types-interfaces-enums';

const HTTP_STATUS_KEYS: Record<number, string> = {
    400: 'http-400',
    401: 'http-401',
    403: 'http-403',
    404: 'http-404',
    405: 'http-405',
    408: 'http-408',
    409: 'http-409',
    429: 'http-429',
    500: 'http-500',
    502: 'http-502',
    503: 'http-503',
    504: 'http-504',
};

/**
 * The default interceptor examines all HTTP requests & responses and automatically updates the requesting state
 * and shows error notifications.
 */
@Injectable()
export class DefaultInterceptor implements HttpInterceptor {
    private readonly tokenMethod: AdminUiConfig['tokenMethod'] = 'cookie';
    private readonly authTokenHeaderKey: string;

    protected destroyRef = inject(DestroyRef);
    private injector = inject(Injector);
    private authService = inject(AuthService);
    private router = inject(Router);
    private dataService = inject(DataService);
    private localStorageService = inject(LocalStorageService);
    /** Guards against N parallel logouts/navigations when several responses report FORBIDDEN at once. */
    private forbiddenLogoutInFlight = false;

    constructor(

    ) {
        this.tokenMethod = getAppConfig().tokenMethod;
        this.authTokenHeaderKey = getAppConfig().authTokenHeaderKey || DEFAULT_AUTH_TOKEN_HEADER_KEY;
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.dataService.client.startRequest().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        return this.dataService.client.uiState().single$.pipe(
            switchMap(({ uiState }) => {
                // console.log("CONTENT LANGUAGE From DefaultInterceptor", uiState?.contentLanguage);

                if (!req.url) {
                    return next.handle(req);
                }
                const request = req.clone({
                    setParams: {
                        languageCode: String(uiState?.contentLanguage ?? ''),
                    },
                });
                return next.handle(request);
            }),
            tap({
                next: event => {
                    if (event instanceof HttpResponse) {
                        this.checkForAuthToken(event);
                        this.notifyOnError(event);
                        this.dataService.client.completeRequest().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                    }
                },
                error: err => {
                    if (err instanceof HttpErrorResponse) {
                        this.notifyOnError(err);
                        this.dataService.client.completeRequest().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                    } else {
                        this.handleNonHttpError(err);
                    }
                },
            }),
        );
    }

    private notifyOnError(response: HttpResponse<any> | HttpErrorResponse) {
        if (response instanceof HttpErrorResponse) {
            if (response.status === 0) {
                const { apiHost, apiPort } = getAppConfig();
                this.displayErrorNotification(_(`error.could-not-connect-to-server`), {
                    url: `${apiHost}:${apiPort}`,
                });
            } else if (response.status === 503 && response.url?.endsWith('/health')) {
                this.displayErrorNotification(_(`error.health-check-failed`));
            } else {
                const extracted = this.extractErrorFromHttpResponse(response);
                if (extracted !== null) {
                    this.displayErrorNotification(extracted);
                } else {
                    const key = this.getHttpStatusTranslationKey(response.status);
                    this.displayErrorNotification(_(`error.${key}`), {
                        status: response.status,
                        url: response.url ?? '',
                    });
                    console.error('HTTP error', response);
                }
            }
        } else {
            // GraphQL errors still return 200 OK responses, but have the actual error message
            // inside the body of the response.
            const graqhQLErrors = response.body.errors;
            if (graqhQLErrors && Array.isArray(graqhQLErrors)) {
                const firstCode: string = graqhQLErrors[0].extensions?.code;

                if (firstCode === ErrorCode.FORBIDDEN) {
                    this.handleForbidden(graqhQLErrors[0]);
                }
                else if (firstCode === ErrorCode.FORBIDDEN_RESOURCE) {
                    // DEV-3106: `me` is the session probe (AuthGuard / "Continue as {user}"). FORBIDDEN_RESOURCE there
                    // only means "no session behind this token"; the caller handles it, a permission toast would mislead.
                    if (!this.isSessionProbe(graqhQLErrors[0])) {
                        this.displayErrorNotification(_(`error.403-forbidden-resource`));
                    }
                }
                else if (firstCode === ErrorCode.CHANNEL_NOT_FOUND) {
                    const message = graqhQLErrors.map(err => err.message).join('\n');
                    this.displayErrorNotification(message);
                    this.localStorageService.clear('activeChannelToken');
                } else {
                    const message = graqhQLErrors.map(err => err.message).join('\n');
                    this.displayErrorNotification(message);
                }
            }
        }
    }

    /**
     * A FORBIDDEN GraphQL error means the session is gone: log out once and send the user to the login page.
     *
     * DEV-3106:
     * - Several queries usually fail together after a session dies; only the first one triggers the logout and
     *   the navigation, the rest are ignored while that logout is in flight.
     * - When the user is already on the login page (e.g. "Continue as {user}" validating a stale token) no
     *   navigation happens at all. Navigating would capture `/login` itself as the post-login redirect target
     *   and bounce the user back to the login page after a successful manual login.
     */
    private handleForbidden(error: { path?: readonly (string | number)[] }): void {
        if (this.forbiddenLogoutInFlight) {
            return;
        }
        this.forbiddenLogoutInFlight = true;
        const isOnLoginPage = this.isOnLoginPage();
        this.authService.logOut().subscribe({
            next: () => {
                this.forbiddenLogoutInFlight = false;
                if (isOnLoginPage) {
                    return;
                }
                const path = (error.path ?? []).join(' > ');
                this.displayErrorNotification(_(`error.403-forbidden`), { path });
                this.router.navigate(['/login'], {
                    queryParams: {
                        [AUTH_REDIRECT_PARAM]: btoa(this.router.url),
                    },
                });
            },
            error: () => {
                this.forbiddenLogoutInFlight = false;
            },
        });
    }

    private isOnLoginPage(): boolean {
        return window.location.pathname.includes('login');
    }

    /** True when the failing GraphQL field is the top-level `me` query used to validate the stored session. */
    private isSessionProbe(error: { path?: readonly (string | number)[] }): boolean {
        return error?.path?.length === 1 && error.path[0] === 'me';
    }

    /**
     * Extracts error message from HTTP response. Returns null when the error is a plain
     * HTTP status error (no GraphQL errors in body), so the caller can use status-based translation.
     */
    private extractErrorFromHttpResponse(response: HttpErrorResponse): string | null {
        if (response.error && response.error.errors) {
            const errors = response.error.errors;
            if (Array.isArray(errors)) {
                return errors.map((e: { message?: string }) => e.message).join('\n');
            }
        }
        if (response.error?.message) {
            return response.error.message;
        }
        return null;
    }

    /**
     * Maps HTTP status codes to translation key suffixes (e.g. 504 -> 'http-504').
     */
    private getHttpStatusTranslationKey(status: number): string {
        return HTTP_STATUS_KEYS[status] ?? 'http-generic';
    }

    /**
     * Handles non-HttpErrorResponse errors (e.g. Apollo "Response not successful: Received status code X").
     */
    private handleNonHttpError(err: unknown): void {
        const message = err instanceof Error ? err.message : String(err);
        const statusMatch = message.match(/status code (\d+)/i);
        if (statusMatch) {
            const status = statusMatch[1];
            this.displayErrorNotification(_(`error.graphql-status`), { status });
            console.error('GraphQL/HTTP error', err);
        } else {
            this.displayErrorNotification(message);
            console.error('Unknown error', err);
        }
    }

    /**
     * We need to lazily inject the NotificationService since it depends on the I18nService which
     * eventually depends on the HttpClient (used to load messages from json files). If we were to
     * directly inject NotificationService into the constructor, we get a cyclic dependency.
     */
    private displayErrorNotification(message: string, vars?: Record<string, any>): void {
        const notificationService = this.injector.get<NotificationService>(NotificationService);
        notificationService.error(message, vars);
    }

    /**
     * If the server is configured to use the "bearer" tokenMethod, each response should be checked
     * for the existence of an auth token.
     */
    private checkForAuthToken(response: HttpResponse<any>) {
        if (this.tokenMethod === 'bearer') {
            const authToken = response.headers.get(this.authTokenHeaderKey);
            if (authToken) {
                this.localStorageService.store(AUTH_TOKEN_KEY, authToken);
            }
        }
    }
}